import React, { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { ShoppingCart, Star, Heart, Search, Filter, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { API_URL, UPLOADS_API_URL } from './config';
import gashome from './assets/gashome.webp';

const XGOMarketplace = () => {
  const [showCart, setShowCart] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/productdefinition/full_products_definations`);
      const data = await response.json();
      setAllProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`API_URL/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await fetch(`API_URL/categories/getsubcategories/${categoryId}`);
      const data = await response.json();
      setSubCategories(data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product_id === product.product_id);
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const toggleWishlist = (productId) => {
    setWishlist((prev) => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const filteredProducts = allProducts.filter((product) => {
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesSubCategory = !selectedSubCategory || product.sub_category === selectedSubCategory;
    const matchesSearch = product.product_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSubCategory && matchesSearch;
  });

  return (
    <div className="min-vh-100 bg-light">
      {/* Navigation Bar */}
      <nav className="navbar navbar-expand-lg fixed-top shadow-sm" style={{ backgroundColor: '#ffc000' }}>
        <div className="container">
          <a className="navbar-brand fw-bold text-dark" href="#">
            XGO Marketplace
          </a>
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-dark">Sign In</button>
            <button className="btn btn-dark">Sign Up</button>
            <button className="btn position-relative" onClick={() => setShowCart(true)}>
              <ShoppingCart />
              {cart.length > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-dark text-white py-5 mb-4" style={{ marginTop: '56px' }}>
        <div className="container">
          <h1 className="display-4 fw-bold mb-4">Discover Amazing Products</h1>
          <p className="lead mb-4">Your one-stop shop for software licenses and gas solutions</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="container mb-4">
        <div className="row g-3">
          <div className="col-md-8">
            <div className="input-group">
              <input
                type="search"
                className="form-control"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="btn btn-dark">
                <Search size={20} />
              </button>
            </div>
          </div>
          <div className="col-md-4">
            <button className="btn btn-outline-dark w-100" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="me-2" />
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="container mb-4">
        <div className="row g-3">
          <div className="d-flex flex-wrap gap-2 mb-3">
            {categories.map((category) => (
              <button
                key={category.categories_id}
                className={`btn ${selectedCategory === category.categories_id ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => {
                  setSelectedCategory(category.categories_id);
                  fetchSubCategories(category.categories_id);
                }}
              >
                {category.category_name}
              </button>
            ))}
          </div>

          {selectedCategory && (
            <div className="mb-4">
              <h5 className="mb-3">
                {categories.find(cat => cat.categories_id === selectedCategory)?.category_name}
              </h5>
              <div className="d-flex flex-wrap gap-2">
                {subCategories.map((subCategory) => (
                  <button
                    key={subCategory.sub_category_id}
                    className={`btn btn-sm ${selectedSubCategory === subCategory.sub_category_name ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setSelectedSubCategory(subCategory.sub_category_name)}
                  >
                    {subCategory.sub_category_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Products Display */}
      <div className="container mb-5">
        <div className="row row-cols-1 row-cols-md-4 g-4">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <AlertCircle size={48} className="text-muted mb-3" />
              <h4>No Products Found</h4>
              <p>Please adjust your search or filter criteria.</p>
            </div>
          ) : (
            filteredProducts.map(product => (
              <div key={product.product_id} className="col">
                <div className="card h-100 shadow-sm">
                  <img
                    className="card-img-top"
                    src={`${UPLOADS_API_URL}/${product.uploaded_product_image_ref}`}
                    alt={product.product_name}
                    style={{ height: "200px", objectFit: "contain" }}
                    onClick={() => {
                      setSelectedProduct(product);
                      setShowProductModal(true);
                    }}
                  />
                  <div className="card-body">
                    <h6 className="card-title mb-1">{product.product_name}</h6>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">${product.selling_price}</span>
                      <div className="d-flex align-items-center">
                        <Star size={16} fill="#ffc000" />
                        <span className="ms-1">{product.rating || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Product Modal */}
      {showProductModal && selectedProduct && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedProduct.product_name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowProductModal(false)}></button>
              </div>
              <div className="modal-body">
                <img
                  src={`${UPLOADS_API_URL}/${selectedProduct.uploaded_product_image_ref}`}
                  alt={selectedProduct.product_name}
                  className="img-fluid rounded"
                />
                <h4 className="mt-3">${selectedProduct.selling_price}</h4>
                <p>{selectedProduct.description_notes}</p>
                <button className="btn btn-warning" onClick={() => {
                  addToCart(selectedProduct);
                  setShowProductModal(false);
                }}>
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-dark text-white py-4 mt-auto">
        <div className="container">
          <div className="row">
            <div className="col-md-4">
              <h5>About XGO Marketplace</h5>
              <p className="small">Your trusted destination for quality products</p>
            </div>
            <div className="col-md-4">
              <h5>Quick Links</h5>
              <ul className="list-unstyled">
                <li><a href="#" className="text-white">About Us</a></li>
                <li><a href="#" className="text-white">Contact</a></li>
                <li><a href="#" className="text-white">Terms & Conditions</a></li>
              </ul>
            </div>
            <div className="col-md-4">
              <h5>Newsletter</h5>
              <div className="input-group mb-3">
                <input type="email" className="form-control" placeholder="Enter your email" />
                <button className="btn btn-warning" type="button">Subscribe</button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default XGOMarketplace;